import { Flex, Spinner, Tooltip, useBoolean } from '@chakra-ui/react';
import { ReactComponent as AutoSaveIcon } from 'assets/icons/autosave.svg';
import { ReactElement, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useDebouncedCallback } from '../../../../hooks/useDebounce';
import { PersistManagerContext } from '../../store/persistManager';
import RefreshAlertDialog from './components/RefreshAlertDialog';

export const AutosaveIndicator = (): ReactElement => {
  const { t } = useTranslation('common_navbar');
  const { saving, lastError } = useContext(PersistManagerContext);
  const [savingState, setSavingState] = useBoolean(false);
  const isError = !!lastError;
  const testid = 'autosave-indicator';

  const debouncedUpdateSavingState = useDebouncedCallback(() => {
    setSavingState.off();
  }, 500);

  useEffect(() => {
    if (saving && !savingState) {
      setSavingState.on();
    }

    if (!saving && savingState) {
      debouncedUpdateSavingState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving, savingState, debouncedUpdateSavingState]);

  const tooltipLabel = savingState ? t('alert.autosave.saving') : t('alert.autosave.allChangesSaved');
  return (
    <Tooltip label={tooltipLabel} hasArrow placement='bottom' bg='black'>
      <Flex
        height='32px'
        background={'transparent'}
        borderRadius='base'
        color='font.500'
        align='center'
        justify='center'
        data-testid={testid}
        p={2}
        gap={2}
        tabIndex={0}
      >
        {savingState ? <Spinner size='xs' aria-label={tooltipLabel} /> : <AutoSaveIcon aria-label={tooltipLabel} />}
        <RefreshAlertDialog
          isOpen={isError}
          title={t('modals.refresh.title')}
          description={t('modals.refresh.description')}
        />
      </Flex>
    </Tooltip>
  );
};
